/**
 * DOM utility functions for vanilla TypeScript
 */

// Exported function for backwards compatibility
export function createElement<T extends keyof HTMLElementTagNameMap>(
  tag: T,
  options: {
    className?: string
    id?: string
    textContent?: string
    innerHTML?: string
    attributes?: Record<string, string>
    styles?: Partial<CSSStyleDeclaration>
  } = {}
): HTMLElementTagNameMap[T] {
  return DOMUtils.createElement(tag, options)
}

export class DOMUtils {
  /**
   * Create an element with attributes and content
   */
  static createElement<T extends keyof HTMLElementTagNameMap>(
    tag: T,
    options: {
      className?: string
      id?: string
      textContent?: string
      innerHTML?: string
      attributes?: Record<string, string>
      styles?: Partial<CSSStyleDeclaration>
    } = {}
  ): HTMLElementTagNameMap[T] {
    const element = document.createElement(tag)
    
    if (options.className) element.className = options.className
    if (options.id) element.id = options.id
    if (options.textContent) element.textContent = options.textContent
    if (options.innerHTML) element.innerHTML = options.innerHTML
    
    if (options.attributes) {
      Object.entries(options.attributes).forEach(([key, value]) => {
        element.setAttribute(key, value)
      })
    }
    
    if (options.styles) {
      Object.entries(options.styles).forEach(([key, value]) => {
        if (value !== undefined) {
          (element.style as any)[key] = value
        }
      })
    }
    
    return element
  }

  /**
   * Clear all children from an element
   */
  static clearElement(element: HTMLElement): void {
    while (element.firstChild) {
      element.removeChild(element.firstChild)
    }
  }

  /**
   * Add event listener with automatic cleanup
   */
  static addEventListenerWithCleanup(
    element: HTMLElement,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): () => void {
    element.addEventListener(event, handler, options)
    return () => element.removeEventListener(event, handler, options)
  }

  /**
   * Wait for DOM to be ready
   */
  static waitForDOM(): Promise<void> {
    return new Promise((resolve) => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => resolve())
      } else {
        resolve()
      }
    })
  }

  /**
   * Animate element with CSS transitions
   */
  static animate(
    element: HTMLElement,
    properties: Partial<CSSStyleDeclaration>,
    duration: number = 300
  ): Promise<void> {
    return new Promise((resolve) => {
      element.style.transition = `all ${duration}ms ease-in-out`
      
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== undefined) {
          (element.style as any)[key] = value
        }
      })
      
      setTimeout(() => {
        element.style.transition = ''
        resolve()
      }, duration)
    })
  }

  /**
   * Scroll to element smoothly
   */
  static scrollToElement(element: HTMLElement, offset: number = 0): void {
    const y = element.getBoundingClientRect().top + window.pageYOffset + offset
    window.scrollTo({ top: y, behavior: 'smooth' })
  }
}
